import { Check } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useSite, useContent } from "@/context/SiteContext";
import { Container, Eyebrow, LLink, ArrowLink } from "@/components/common";
import { Reveal, LineReveal, Stagger, Item } from "@/components/motion/Reveal";

export function CorporateHub() {
  const { t, lang, L } = useLang();
  const { meeting_rooms } = useSite();
  const c = useContent();
  const featured = meeting_rooms.slice(0, 3);

  return (
    <section data-testid="home-corporate" className="bg-ink on-dark py-20 md:py-32 overflow-hidden">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <Reveal><Eyebrow light className="mb-6">{t.home.corpEyebrow}</Eyebrow></Reveal>
            <LineReveal as="h2" lines={t.home.corpTitle.split("|")} className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-bone" />
            <Reveal delay={0.15}><p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-bone/65">{c("emp_text", lang)}</p></Reveal>
            <Stagger className="mt-10 space-y-3">
              {t.home.corpFeatures.map((f) => (
                <Item key={f} className="flex items-center gap-3 text-sm text-bone/85">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gold/50 text-gold"><Check size={10} /></span>
                  {f}
                </Item>
              ))}
            </Stagger>
            <Reveal delay={0.3} className="mt-10 flex flex-wrap gap-4">
              <LLink to="corporate" data-testid="corporate-cta" className="btn btn-light">{t.home.corpCta}</LLink>
              <LLink to="meetingRooms" data-testid="corporate-secondary" className="btn btn-ghost-light">{t.home.corpSecondary}</LLink>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
            {featured[0] && (
              <Reveal className="col-span-2 img-zoom aspect-[16/10]">
                <LLink to="meetingRoomDetail" params={{ slug: featured[0].slug }} data-testid={`home-sala-${featured[0].slug}`} className="block h-full relative">
                  <img src={featured[0].image} alt={L(featured[0].name)} loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute bottom-4 left-4 bg-ink/70 backdrop-blur px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">{L(featured[0].name)} · {featured[0].area_m2} m² · {t.corporate.upTo} {featured[0].capacity?.teatro || featured[0].capacity?.imperial} {t.corporate.people}</span>
                </LLink>
              </Reveal>
            )}
            {featured.slice(1).map((m, i) => (
              <Reveal key={m.id} delay={0.1 * (i + 1)} className="img-zoom aspect-[4/3]">
                <LLink to="meetingRoomDetail" params={{ slug: m.slug }} data-testid={`home-sala-${m.slug}`} className="block h-full relative">
                  <img src={m.image} alt={L(m.name)} loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute bottom-3 left-3 bg-ink/70 backdrop-blur px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-bone">{L(m.name)}</span>
                </LLink>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="col-span-2 flex justify-end">
              <LLink to="meetingRooms" data-testid="home-salas-all"><ArrowLink light>{t.corporate.salasCta}</ArrowLink></LLink>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}
